import Vue from 'vue'
import clientSubscription from '@/utils/client.subscription'

const MUTATION_TYPES = {
  UPDATE_QUIZ_RESPONSE: 'UPDATE_QUIZ_RESPONSE',
  CLEAR_QUIZ_RESPONSE: 'CLEAR_QUIZ_RESPONSE',
  SET_RESULT_KEY_PREFIX: 'SET_RESULT_KEY_PREFIX',
}

export const RESULT_KEY_PREFIX = {
  QUIZ: 'QUIZ_RESULTS',
  ANSWER: 'ANSWER_RESULTS',
}

const _state = {
  // Per-slide plugin-quiz results for the streak overlay, keyed by slideId.
  quizResults: {},
  // kvSync prefix the native recovery fetch should read, snapshotted at subscribe time.
  resultKeyPrefix: RESULT_KEY_PREFIX.QUIZ,
}

const _getters = {
  quizResultBySlide: (state) => (slideId) => state.quizResults[slideId],
}

const actions = {
  // setPrefix=false subscribes additively (fallback / overlay) without claiming the native recovery prefix.
  // TODO: for future development, send version = slide.version to keep the historical quiz result.
  subscribeResultResponseAction: async ({ commit, dispatch }, { audienceId, prefix, setPrefix = true }) => {
    if (setPrefix) commit(MUTATION_TYPES.SET_RESULT_KEY_PREFIX, prefix)
    await dispatch('kvSync/subscribeKeyAction', {
      key: `${prefix}/${audienceId}`,
    }, {
      root: true,
    })
  },
}

const mutations = {
  [MUTATION_TYPES.UPDATE_QUIZ_RESPONSE](state, quizResult) {
    Vue.set(state.quizResults, quizResult.slide, quizResult)
  },
  [MUTATION_TYPES.CLEAR_QUIZ_RESPONSE](state, slideId) {
    Vue.delete(state.quizResults, slideId)
  },
  [MUTATION_TYPES.SET_RESULT_KEY_PREFIX](state, prefix) {
    state.resultKeyPrefix = prefix
  },
}

// Native quiz (SubmitQuizResponse): top-level slide -> resolve the parked promise.
// Slide-scoped key stops a stale earlier-slide result resolving the wrong promise.
const resolveNativeQuizResult = (parsed) => {
  const keySubscrible = `${clientSubscription.constantObjectSubcriptionType.submitQuizAnswerType}-${parsed.slide}`
  const parked = clientSubscription.objectSubcription[keySubscrible]
  if (!parked) return
  if (!parsed.id) parsed.id = 1
  parked.resolve(parsed)
  delete clientSubscription.objectSubcription[keySubscrible]
}

const commitPluginStreak = (store, result) => {
  if (!result || result.participantId !== store.state.audienceId) return
  if (!result.slideId || !result.currentStreak) return
  store.commit(`quiz/${MUTATION_TYPES.UPDATE_QUIZ_RESPONSE}`, {
    slide: result.slideId,
    streak: result.currentStreak,
    lastStreak: result.lastStreak,
    longestStreak: result.longestStreak,
    point: result.points,
    correct: result.verdict === 'CORRECT',
  })
}

const plugins = {
  updateQuizResponse: (store) => {
    store.subscribeAction((action) => {
      if (action.type !== 'kvSync/updateKeyValueAction') return
      const { key, value } = action.payload
      const isResultKey = key.includes('QUIZ_RESULTS/') || key.includes('ANSWER_RESULTS/')
      if (!isResultKey || !value) return
      const parsed = JSON.parse(value)

      if (parsed.slide !== undefined) {
        resolveNativeQuizResult(parsed)
        return
      }

      // Plugin quiz (AnswerResponse): streaks nested per result, no top-level slide.
      if (!store.getters['featureToggles/canUseAnswerResultsPluginOverlay']) return
      if (!Array.isArray(parsed.results)) return
      parsed.results.forEach((result) => commitPluginStreak(store, result))
    })
  },
}

const quizModule = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
  plugins,
}

export default quizModule
